import * as Sentry from '@sentry/react';
import type { ErrorEvent, EventHint, Scope } from '@sentry/react';

/**
 * Initialize Sentry for global error handling
 * Only captures critical errors (error and fatal levels)
 */
export function initSentry() {
	// Only initialize Sentry in production or when DSN is provided
	const dsn = import.meta.env.VITE_SENTRY_DSN;
	const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

	if (!dsn) {
		console.warn('Sentry DSN not provided, skipping initialization');
		return;
	}

	Sentry.init({
		dsn,
		environment,
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.replayIntegration({
				// Only capture replays for error sessions
				maskAllText: true,
				blockAllMedia: true,
			}),
		],

		// Performance monitoring
		tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

		// Session replay
		replaysSessionSampleRate: 0.0, // Don't capture normal sessions
		replaysOnErrorSampleRate: 1.0, // Capture all error sessions

		// Only send critical errors to minimize noise
		beforeSend(event: ErrorEvent, hint: EventHint) {
			// Only capture errors and fatal events
			if (event.level !== 'error' && event.level !== 'fatal') {
				return null;
			}

			// Filter out non-critical errors
			const error = hint.originalException;
			if (error instanceof Error) {
				const message = error.message.toLowerCase();

				// Skip common non-critical errors
				const skipPatterns = [
					'network error',
					'loading css chunk failed',
					'loading chunk',
					'non-error promise rejection',
					'script error',
					'network request failed',
					'fetch error',
				];

				if (skipPatterns.some((pattern) => message.includes(pattern))) {
					return null;
				}
			}

			// Add user context if available (without sensitive data)
			const userInfo = sessionStorage.getItem('fleet-user-info');
			if (userInfo) {
				try {
					const user = JSON.parse(userInfo);
					event.user = {
						id: user.id,
						email: user.email?.replace(/(.{2})(.*)(@.*)/, '$1***$3'), // Mask email
					};
				} catch {
					// Ignore parsing errors
				}
			}

			// Add application context
			event.tags = {
				...event.tags,
				component: 'fleet-mtf-frontend',
				version: import.meta.env.VITE_APP_VERSION || 'unknown',
			};

			return event;
		},

		// Note: captureUnhandledRejections is enabled by default in modern Sentry versions

		// Set release information
		release: import.meta.env.VITE_APP_VERSION,
	});
}

/**
 * Manually capture critical errors with context
 */
export function captureError(error: Error, context?: Record<string, unknown>) {
	Sentry.withScope((scope: Scope) => {
		if (context) {
			Object.entries(context).forEach(([key, value]) => {
				scope.setContext(key, value as unknown as Sentry.Context);
			});
		}
		scope.setLevel('error');
		Sentry.captureException(error);
	});
}

/**
 * Set user context for error tracking
 */
export function setSentryUser(user: {
	id: string;
	email?: string;
	role?: string;
}) {
	Sentry.setUser({
		id: user.id,
		email: user.email?.replace(/(.{2})(.*)(@.*)/, '$1***$3'), // Mask email for privacy
		role: user.role,
	});
}

/**
 * Clear user context on logout
 */
export function clearSentryUser() {
	Sentry.setUser(null);
}

/**
 * Add breadcrumb for debugging context
 */
export function addBreadcrumb(
	message: string,
	category: string,
	level: Sentry.SeverityLevel = 'info',
) {
	Sentry.addBreadcrumb({
		message,
		category,
		level,
		timestamp: Date.now() / 1000,
	});
}
